import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserInfo } from 'pages/UserProvider';
import { captureEvent } from 'utils/posthog';

const ACE_ROUTE_PATTERN = /^\/ace(\/|$)/;
const DWELL_MS = 1000;

/**
 * Captures an `ace_page_viewed` PostHog event once the user has stayed on any
 * /ace route for at least one second (ENG-573). The dwell threshold filters
 * out accidental clicks and immediate bounces; leaving the route before it
 * elapses cancels the capture. Fires on every qualifying visit — the PostHog
 * workflow's per-person trigger masking dedupes the Slack notifications.
 *
 * Identity is duplicated onto event properties (rather than relying on person
 * properties) so the workflow can exclude staff +alias logins on user_email.
 */
const useAceDwellCapture = (): void => {
  const { pathname } = useLocation();
  const { user, company } = useUserInfo();
  const userId = user?.id;
  const userEmail = user?.username;
  const companyId = company?.id;
  const companyName = company?.companyName;

  useEffect(() => {
    if (!ACE_ROUTE_PATTERN.test(pathname) || !userId) {
      return;
    }
    const timer = setTimeout(() => {
      captureEvent('ace_page_viewed', {
        path: pathname,
        user_id: userId,
        user_email: userEmail,
        company_id: companyId,
        company_name: companyName,
      });
    }, DWELL_MS);
    return () => clearTimeout(timer);
  }, [pathname, userId, userEmail, companyId, companyName]);
};

export default useAceDwellCapture;
